import * as echarts from 'echarts/core'
import { BarChart, FunnelChart, LineChart, PieChart } from 'echarts/charts'
import { GridComponent, LegendComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'

echarts.use([BarChart, LineChart, PieChart, FunnelChart, GridComponent, LegendComponent, CanvasRenderer])

/**
 * A chart as a PNG data URL, drawn off-screen.
 *
 * FOR DOCUMENTS, NOT SCREENS. A chart bound for a PDF is drawn once at a fixed size and
 * never resized, so it wants an instance of its own rather than the live `Chart` component.
 *
 * DRAWN BY ECHARTS, NOT PHOTOGRAPHED. The rest of the sheet goes through html2canvas, but a
 * chart rendered that way is only as sharp as the page capture — and a chart is the one
 * thing on a sheet a reader zooms into. Canvas also shapes Arabic labels correctly, which
 * is the same reason the page itself is rasterised rather than typeset by jsPDF.
 *
 * `features/dashboard/reportCharts.js` has the same helper for the statistics report; this
 * is it without that report's own theme, so any feature can use it.
 *
 * @param {object} option        an ECharts option, from `components/chartTheme`
 * @param {number} [width]       CSS pixels
 * @param {number} [height]
 * @param {number} [pixelRatio]  3 keeps it crisp when the sheet is scaled onto A4
 * @returns {string} `data:image/png;base64,…`
 */
export function chartToImage(option, { width = 900, height = 320, pixelRatio = 3 } = {}) {
  const host = document.createElement('div')
  host.setAttribute('aria-hidden', 'true')
  host.style.cssText = `width:${width}px;height:${height}px;position:absolute;left:-10000px;top:0`
  document.body.appendChild(host)

  const chart = echarts.init(host, null, { renderer: 'canvas', devicePixelRatio: pixelRatio })
  try {
    chart.setOption({
      ...option,
      /* No entrance animation: the PNG is taken the moment the option is set, and an
         animating chart photographs as a chart partway through arriving. */
      animation: false,
    })
    return chart.getDataURL({ type: 'png', pixelRatio, backgroundColor: '#ffffff' })
  } finally {
    chart.dispose()
    host.remove()
  }
}
